/**
 * fetchWithTimeout — drop-in replacement for `fetch` that aborts the
 * request after a hard timeout.
 *
 * Why: bare `fetch` has no timeout and will hang as long as the network
 * keeps the connection open. A 502 mid-stream, a service worker that
 * never resolves, a pod under memory pressure — any of these can leave
 * the UI stuck on a loading spinner forever. Round 3 of post-deploy
 * fixes patched the search endpoint with an inline AbortController; this
 * utility makes the pattern reusable across the codebase.
 *
 * Behavior:
 * - Default timeout is 30s — generous for normal API calls, short
 *   enough that a hung request doesn't outlast a user's patience.
 * - Override per call: `fetchWithTimeout(url, init, 60000)`.
 * - Honors a caller-supplied `init.signal` — if the caller aborts their
 *   own controller, we abort too (and clean up our timer).
 * - On timeout, fetch rejects with `AbortError`. Callers handle it like
 *   any other fetch error in their existing try/catch.
 *
 * Usage:
 *   import { fetchWithTimeout } from '@/utils/fetchWithTimeout';
 *
 *   const res = await fetchWithTimeout('/api/auth/login', {
 *     method: 'POST',
 *     body: JSON.stringify({ email, password }),
 *   });
 *
 *   // Long-running admin endpoint:
 *   const res = await fetchWithTimeout(`/api/admin/apartments`, init, 60000);
 */

const DEFAULT_TIMEOUT_MS = 30000;

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // Honor caller's own AbortSignal so external cancellation (e.g. component
  // unmount) still propagates. Without this, the caller's abort would only
  // fire if `init.signal` was the actual signal we passed to fetch — but
  // we replace it with our own.
  const callerSignal = init.signal;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
